import { useTranslation } from 'react-i18next';
import { IpponBadge, IpponCard, IpponHSpace, IpponText, IpponVSpace } from '@ippon-ui/react';
import { useOptional } from '@/generic/react-tools';
import { type Description, ExampleCompliance, type Severity, useSeverityText } from '@/rule';
import type { Optional } from '@/generic/functional';
import type { DraftExample, DraftRule } from '@/rules-management/domain/DraftRule.ts';

const DraftRuleSeverityBadge = ({ severity }: { severity: Severity }) => (
  <IpponBadge dataSelector="draft-rule.severity">{useSeverityText(severity)}</IpponBadge>
);

const DraftRuleSeverityComponent = ({ severity }: { severity: Optional<Severity> }) =>
  useOptional({
    optional: severity,
    onValuated: (severity) => <DraftRuleSeverityBadge severity={severity} />,
  });

export const DraftRuleHeadingComponent = ({ draft }: { draft: DraftRule }) => {
  const { t } = useTranslation('rules-management');

  const title = draft.title
    .map((title) => title.value)
    .orElseGet(() => t('rules-management.rules.import.result.untitled'));

  return (
    <IpponHSpace gap={8} align="middle">
      <IpponText variant="body" size="small" dataSelector="draft-rule.title">
        {title}
      </IpponText>
      <DraftRuleSeverityComponent severity={draft.severity} />
    </IpponHSpace>
  );
};

const complianceToColor = (compliance: ExampleCompliance) => {
  switch (compliance) {
    case ExampleCompliance.COMPLIANT:
      return 'success' as const;
    case ExampleCompliance.NON_COMPLIANT:
      return 'error' as const;
  }
};

const DraftExampleDescriptionComponent = ({ description }: { description: Optional<string> }) =>
  useOptional({
    optional: description,
    onValuated: (description) => (
      <IpponText
        variant="body"
        size="small"
        weight="bold"
        dataSelector="draft-rule.example.description"
      >
        {description}
      </IpponText>
    ),
  });

const DraftExampleSnippetComponent = ({ snippet }: { snippet: Optional<string> }) =>
  useOptional({
    optional: snippet,
    onValuated: (snippet) => (
      <IpponText variant="body" size="small" dataSelector="draft-rule.example.snippet">
        {snippet}
      </IpponText>
    ),
  });

const DraftExampleComponent = ({ example }: { example: DraftExample }) => (
  <IpponCard
    dataSelector="draft-rule.example"
    color={example.compliance.map(complianceToColor).orUndefined()}
  >
    <IpponVSpace gap={8}>
      <DraftExampleDescriptionComponent description={example.description} />
      <DraftExampleSnippetComponent snippet={example.snippet} />
    </IpponVSpace>
  </IpponCard>
);

const DraftRuleDescriptionComponent = ({ description }: { description: Optional<Description> }) =>
  useOptional({
    optional: description,
    onValuated: (description) => (
      <IpponText
        variant="body"
        size="small"
        color="neutral-tertiary-inversed"
        dataSelector="draft-rule.description"
      >
        {description.value}
      </IpponText>
    ),
  });

export const DraftRuleDetailsComponent = ({ draft }: { draft: DraftRule }) => (
  <>
    <DraftRuleDescriptionComponent description={draft.description} />
    {draft.documentedExamples.map((example, index) => (
      <DraftExampleComponent key={`${draft.id.value}-example-${index}`} example={example} />
    ))}
  </>
);
